import React from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'uuid/v1';

import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import { initialize } from 'redux-form';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { Tab, Typography } from '@material-ui/core';
import Tabs from '@material-ui/core/Tabs';

import {
  AppHeader,
  CircularLoader,
  TabPanel,
  Payments,
  Integrations,
  SelectCompanyBox
} from 'components';

import {
  CREATE_PAYMENT_SETTING,
  UPDATE_PAYMENT_SETTING,
  CREATE_INTEGRATIONS_SETTINGS,
  UPDATE_INTEGRATIONS_SETTINGS,
  CREATE_GOOGLE_ANALYTICS_SETTINGS,
  UPDATE_GOOGLE_ANALYTICS_SETTINGS,
  SET_EMAIL_TEMPLATE
} from 'graphql/mutations/settings';

import { USER_SETTINGS } from 'graphql/queries/settings';

import {
  prepareAllPaymentsSettingsData,
  getReceivedPaymentsData,
  getDeletedPaymentsSettings,
  getReceivedIntegrationsData,
  getTrekkSoftSettings,
  getDeletedIntegrationsSettings,
  getTrekkSoftCategoriesMapping,
  prepareCategoriesMappingData,
  getExpediaSettings,
  getBookingSettings
} from 'utils/prepareUserSettingsData';

import GoogleAnalyticsForm from '../../components/google-analytics-form/GoogleAnalyticsForm';
import EmailSettingsForm from '../../components/email-settings-form/EmailSettingsForm';

import { resetDeletedIntegrationSettings } from '../../actions/userSettings';

import useStyles from './styles';

function verticalTabProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`
  };
}

const PAYMENTS_TAB_INDEX = 0;
const INTEGRATIONS_TAB_INDEX = 1;
const GOOGLE_ANALYTICS_TAB_INDEX = 2;
const EMAIL_TAB_INDEX = 3;

const SettingsPage = ({ ...props }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const {
    initializeForm,
    deletedPaymentsSettings,
    deletedIntegrationsSettings,
    deletedTrekksoftCategories,
    trekkSoftCategoriesMapping,
    businessOwner,
    resetDeletedIntegrationSettings
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const { data, refetch, networkStatus } = useQuery(USER_SETTINGS, {
    variables: {
      businessOwnerId:
        businessOwner.selectedBusinessOwner.ownerId ||
        businessOwner.selectedBusinessOwner.id
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const [value, setValue] = React.useState(0);
  const [savedForms, setSavedForms] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUpdatePayments, setIsUpdatePayments] = React.useState(false);
  const [isUpdateIntegrations, setIsUpdateIntegrations] = React.useState(false);
  const [isUpdateGoogleAnalytics, setIsUpdateGoogleAnalytics] = React.useState(
    false
  );
  const [isInitializedForm, setIsInitializedForm] = React.useState(false);

  const [createPaymentsSettings] = useMutation(CREATE_PAYMENT_SETTING);
  const [updatePaymentsSettings] = useMutation(UPDATE_PAYMENT_SETTING);
  const [createIntegrationsSettings] = useMutation(
    CREATE_INTEGRATIONS_SETTINGS
  );
  const [updateIntegrationsSettings] = useMutation(
    UPDATE_INTEGRATIONS_SETTINGS
  );

  const [createGoogleAnalyticsSettings] = useMutation(
    CREATE_GOOGLE_ANALYTICS_SETTINGS
  );
  const [updateGoogleAnalyticsSettings] = useMutation(
    UPDATE_GOOGLE_ANALYTICS_SETTINGS
  );

  const [setEmailTemplate] = useMutation(SET_EMAIL_TEMPLATE);

  React.useEffect(() => {
    if (data && networkStatus === 7) {
      setIsLoading(false);
      const paymentsData = getReceivedPaymentsData(data);
      const integrationsData = getReceivedIntegrationsData(data);
      const trekksoftCategoriesMapping = getTrekkSoftCategoriesMapping(data);

      setIsUpdatePayments(!!paymentsData.length);
      setIsUpdateIntegrations(!!integrationsData.length);
      setIsUpdateGoogleAnalytics(!!data.googleAnalyticsSettings);

      const savedForm = {
        paymentsForm: { payments: paymentsData },
        integrationSettingsForm: {
          integrations: integrationsData
        },
        trekkSoftCategoriesMapping: {
          trekkSoftCategoriesMapping: trekksoftCategoriesMapping
        },
        googleAnalyticsForm: { ...data.googleAnalyticsSettings }
      };

      let emailSettingsForm = null;

      if (data.emailTemplates && data.emailTemplates.length) {
        emailSettingsForm = data.emailTemplates[0];
        savedForm.emailSettingsForm = emailSettingsForm;
      }

      setSavedForms(savedForm);

      let initialValues = {
        paymentsForm: {
          payments:
            paymentsData && paymentsData.length
              ? paymentsData
              : [
                  {
                    id: uuid(),
                    paymentSystem: ''
                  }
                ]
        },
        integrationSettingsForm: {
          integrations:
            integrationsData && integrationsData.length
              ? integrationsData
              : [
                  {
                    id: uuid(),
                    integrationPlatform: ''
                  }
                ]
        },
        trekkSoftCategoriesMapping: {
          trekkSoftCategoriesMapping: trekksoftCategoriesMapping
        },
        googleAnalyticsForm: { ...data.googleAnalyticsSettings }
      };

      if (emailSettingsForm) {
        initialValues.emailSettingsForm = emailSettingsForm;
      }

      initializeForms(initialValues);
      setIsInitializedForm(true);
    }
  }, [data]);

  const initializeForms = initialFormsData => {
    Object.keys(initialFormsData).map(field => {
      initializeForm(field, initialFormsData[field]);
    });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const paymentsSubmit = values => {
    if (isUpdatePayments) {
      const dataToStore = prepareAllPaymentsSettingsData(
        isUpdatePayments,
        values
      );
      const deletedSettings = getDeletedPaymentsSettings(
        deletedPaymentsSettings
      );
      updatePaymentsSettings({
        variables: {
          ...dataToStore,
          businessOwnerId:
            businessOwner.selectedBusinessOwner.ownerId ||
            businessOwner.selectedBusinessOwner.id,
          deletedPayPalSettingsId: deletedSettings.PayPal,
          deletedStripeSettingsId: deletedSettings.Stripe,
          deletedPayOnArrivalSettingsId: deletedSettings['Pay on arrival']
        }
      })
      .then(() => {
        enqueueSnackbar(t('Settings have been successfully updated'), {
          variant: 'success'
        });
        initializeForm('paymentsForm', values, false, {
          keepSubmitSucceeded: true
        }); 
      });

    } else {
      const dataToStore = prepareAllPaymentsSettingsData(
        isUpdatePayments,
        values
      );
      createPaymentsSettings({
        variables: {
          ...dataToStore,
          businessOwnerId:
            businessOwner.selectedBusinessOwner.ownerId ||
            businessOwner.selectedBusinessOwner.id
        }
      }).then(() => {
        enqueueSnackbar(t('Settings have been successfully saved'), {
          variant: 'success'
        });
        initializeForm('paymentsForm', values, false, {
          keepSubmitSucceeded: true
        });
      });
    }
  };
  
  const integrationsSubmit = values => {
    setIsLoading(true);

    if (isUpdateIntegrations) {
      const trekksoftSettings = getTrekkSoftSettings(
        isUpdateIntegrations,
        values
      );

      const expediaSettings = getExpediaSettings(isUpdateIntegrations, values);
      const bookingSettings = getBookingSettings(isUpdateIntegrations, values);
      const deletedSettings = getDeletedIntegrationsSettings(
        deletedIntegrationsSettings
      );
      trekksoftSettings.categoriesMapping = prepareCategoriesMappingData(
        isUpdateIntegrations,
        trekkSoftCategoriesMapping
      );

      let variables = {
        businessOwnerId:
          businessOwner.selectedBusinessOwner.ownerId ||
          businessOwner.selectedBusinessOwner.id,
        deletedTrekksoftSettingsId: deletedSettings.Trekksoft,
        deletedTrekksoftCategories: deletedTrekksoftCategories,
        deletedExpediaSettingsId: deletedSettings.Expedia,
        deletedBookingSettingsId: deletedSettings.BOOKING,
        expediaSettings: expediaSettings,
        bookingSettings: bookingSettings
      };

      if (!deletedSettings.Trekksoft && deletedSettings.Trekksoft >= 0) {
        variables['trekksoftSettings'] = trekksoftSettings;
      }

      updateIntegrationsSettings({
        variables: variables
      })
        .then(() => {
          enqueueSnackbar(t('Settings have been successfully updated'), {
            variant: 'success'
          });
          refetch({
            businessOwnerId:
              businessOwner.selectedBusinessOwner.ownerId ||
              businessOwner.selectedBusinessOwner.id
          });
          resetDeletedIntegrationSettings();
        })
        .catch(() => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
          setIsLoading(false);
          resetDeletedIntegrationSettings();
        });
    } else {
      const trekksoftSettings = getTrekkSoftSettings(
        isUpdateIntegrations,
        values
      );

      const expediaSettings = getExpediaSettings(isUpdateIntegrations, values);
      const bookingSettings = getBookingSettings(isUpdateIntegrations, values);

      trekksoftSettings.categoriesMapping = prepareCategoriesMappingData(
        isUpdateIntegrations,
        trekkSoftCategoriesMapping
      );
      createIntegrationsSettings({
        variables: {
          businessOwnerId:
            businessOwner.selectedBusinessOwner.ownerId ||
            businessOwner.selectedBusinessOwner.id,
          trekksoftSettings: trekksoftSettings,
          expediaSettings: expediaSettings,
          bookingSettings: bookingSettings
        }
      })
        .then(() => {
          enqueueSnackbar(t('Settings have been successfully saved'), {
            variant: 'success'
          });
          refetch({
            businessOwnerId:
              businessOwner.selectedBusinessOwner.ownerId ||
              businessOwner.selectedBusinessOwner.id
          });
          resetDeletedIntegrationSettings();
        })
        .catch(() => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
          setIsLoading(false);
          resetDeletedIntegrationSettings();
        });
    }
  };

  const googleAnalyticsSettingsSubmit = values => {
    if (isUpdateGoogleAnalytics) {
      updateGoogleAnalyticsSettings({
        variables: {
          googleAnalyticsSettingsId: values.id,
          googleAnalyticsId: values.googleAnalyticsId,
          businessOwnerId:
            businessOwner.selectedBusinessOwner.ownerId ||
            businessOwner.selectedBusinessOwner.id
        }
      }).then(() => {
        enqueueSnackbar(t('Settings have been successfully updated'), {
          variant: 'success'
        });
        initializeForm('googleAnalyticsForm', values, false, {
          keepSubmitSucceeded: true
        });
      });
    } else {
      createGoogleAnalyticsSettings({
        variables: {
          googleAnalyticsId: values.googleAnalyticsId || '',
          businessOwnerId:
            businessOwner.selectedBusinessOwner.ownerId ||
            businessOwner.selectedBusinessOwner.id
        }
      }).then(() => {
        enqueueSnackbar(t('Settings have been successfully saved'), {
          variant: 'success'
        });
        initializeForm('googleAnalyticsForm', values, false, {
          keepSubmitSucceeded: true
        });
      });
    }
  };

  const emailSettingsSubmit = values => {
    setEmailTemplate({
      variables: {
        subject: values.subject,
        body: values.body,
        ownerId:
          businessOwner.selectedBusinessOwner.ownerId ||
          businessOwner.selectedBusinessOwner.id
      }
    })
      .then(({ data }) => {
        if (data.setEmailTemplate.status) {
          enqueueSnackbar(t('Settings have been successfully updated'), {
            variant: 'success'
          });
          initializeForm('emailSettingsForm', values, false, {
            keepSubmitSucceeded: true
          });
        } else {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
        setIsLoading(false);
      })
      .catch(() => {
        enqueueSnackbar(t('Email address is not verified'), {
          variant: 'error'
        });
      });
  };

  return (
    <>
      <div className={classes.root}>
        <AppHeader />
        <CircularLoader
          isLoading={isLoading}
          className={classes.loaderWrapper}
          loaderClassName={classes.loaderClassName}
        />
        {!businessOwner.selectedBusinessOwner.id && <SelectCompanyBox />}
        {businessOwner.selectedBusinessOwner.id && (
          <div className={classes.toolbar}>
            <div className={classes.toolbarLeft}>
              <Typography variant="h2" className={classes.toolbarTitle}>
                {t('Settings')}
              </Typography>
            </div>
            <div className={classes.toolbarSwitchers}>
              <Tabs
                indicatorColor="primary"
                value={value}
                onChange={handleChange}
                orientation="horizontal"
                 variant="scrollable"
              >
                <Tab
                  classes={{
                    root: classes.customTabRoot,
                    wrapper: classes.customTabWrapper,
                    selected: classes.selected,
                    textColorInherit: classes.textColorInheritCustom
                  }}
                  label={t('Payments')}
                  {...verticalTabProps(PAYMENTS_TAB_INDEX)}
                />
                <Tab
                  classes={{
                    root: classes.customTabRoot,
                    wrapper: classes.customTabWrapper,
                    selected: classes.selected,
                    textColorInherit: classes.textColorInheritCustom
                  }}
                  label={t('Integrations')}
                  {...verticalTabProps(INTEGRATIONS_TAB_INDEX)}
                />
                <Tab
                  classes={{
                    root: classes.customTabRoot,
                    wrapper: classes.customTabWrapper,
                    selected: classes.selected,
                    textColorInherit: classes.textColorInheritCustom
                  }}
                  label={t('Google analytics')}
                  {...verticalTabProps(INTEGRATIONS_TAB_INDEX)}
                />
                <Tab
                  classes={{
                    root: classes.customTabRoot,
                    wrapper: classes.customTabWrapper,
                    selected: classes.selected,
                    textColorInherit: classes.textColorInheritCustom
                  }}
                  label={t('Confirmation Email')}
                  {...verticalTabProps(INTEGRATIONS_TAB_INDEX)}
                />
              </Tabs>
            </div>
          </div>
        )}
        {businessOwner.selectedBusinessOwner.id && isInitializedForm && (
          <div className={classes.content}>
            <TabPanel
              value={value}
              index={PAYMENTS_TAB_INDEX}
              className={classes.tab}
            >
              <Payments
                onSubmit={values => {
                  setSavedForms({
                    ...savedForms,
                    paymentsForm: { ...values }
                  });
                  paymentsSubmit(values);
                }}
              />
            </TabPanel>
            <TabPanel
              value={value}
              index={INTEGRATIONS_TAB_INDEX}
              className={classes.tab}
            >
              <Integrations
                onSubmit={values => {
                  setSavedForms({
                    ...savedForms,
                    integrationSettingsForm: { ...values }
                  });
                  integrationsSubmit(values);
                }}
              />
            </TabPanel>
            <TabPanel
              value={value}
              index={GOOGLE_ANALYTICS_TAB_INDEX}
              className={classes.tab}
            >
              <GoogleAnalyticsForm
                onSubmit={values => {
                  setSavedForms({
                    ...savedForms,
                    googleAnalyticsForm: { ...values }
                  });
                  googleAnalyticsSettingsSubmit(values);
                }}
              />
            </TabPanel>

            <TabPanel value={value} index={EMAIL_TAB_INDEX}>
              <EmailSettingsForm
                onSubmit={values => {
                  setSavedForms({
                    ...savedForms,
                    emailSettingsForm: { ...values }
                  });
                  emailSettingsSubmit(values);
                }}
              />
            </TabPanel>
          </div>
        )}
      </div>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  },
  resetDeletedIntegrationSettings: () => {
    dispatch(resetDeletedIntegrationSettings());
  }
});

const mapStateToProps = state => {
  return {
    businessOwner: state.businessOwner,
    trekkSoftCategoriesMapping: state.form.trekkSoftCategoriesMapping,
    deletedPaymentsSettings: state.userSettings.deletedPaymentsSettings,
    deletedIntegrationsSettings: state.userSettings.deletedIntegrationsSettings,
    deletedTrekksoftCategories: state.userSettings.deletedTrekksoftCategories
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage);
